import type { PayexThunkAction } from 'store/configureStore';

import type { HttpError } from 'constants/index';
import {
  DELETE_FILE_FAILURE,
  DELETE_FILE_REQUEST,
  DELETE_FILE_SUCCESS,
  UPLOAD_DOCUMENT_FAILURE,
  UPLOAD_DOCUMENT_MAX_FILES_FAILURE,
  UPLOAD_DOCUMENT_REQUEST,
  UPLOAD_DOCUMENT_SUCCESS,
  USE_PAYEX_PAYMENTS_CONTROLLER,
} from 'constants/index';
import type { RootState } from 'reducers/types';
import { getFeature, isFetching } from 'selectors';
import { errorNotifier } from 'utils/errorNotifier/errorNotifier';
import { isHttpErrorWithStatus } from 'utils/errors/errors';

const shouldUsePayexService = (state: RootState) =>
  getFeature(state, USE_PAYEX_PAYMENTS_CONTROLLER);

export const fetchUploadFileURL =
  (documentUploadURL: string): PayexThunkAction<Promise<string>> =>
  async (_dispatch, getState, { apiV3, payex }) => {
    const state = getState();
    const service = shouldUsePayexService(state) ? payex : apiV3;

    try {
      const response = await service.payments.fetchFileUploadURL({
        documentUploadURL,
      });

      if (!response.url) {
        throw new Error('documentCollector.error');
      }

      return response.url;
    } catch (error) {
      errorNotifier.notify(error);
      throw error;
    }
  };

export const uploadFile =
  (
    file: File,
    documentUrl: string,
  ): PayexThunkAction<
    Promise<undefined | { type: string; payload?: unknown }>
  > =>
  async (dispatch, getState, { apiV3, payex }) => {
    const state = getState();
    const isFetchingDocuments = isFetching(state, 'documents');
    const service = shouldUsePayexService(state) ? payex : apiV3;

    if (isFetchingDocuments) {
      return;
    }

    dispatch({ type: UPLOAD_DOCUMENT_REQUEST });

    try {
      await service.payments.uploadFileToUrl({
        file,
        url: documentUrl,
      });

      return dispatch({
        type: UPLOAD_DOCUMENT_SUCCESS,
      });
    } catch (error) {
      const { name } = file;

      errorNotifier.notify(error);

      return dispatch({
        type: UPLOAD_DOCUMENT_FAILURE,
        payload: { name },
      });
    }
  };

export const deleteFile =
  (url: string, fileId: string): PayexThunkAction =>
  async (dispatch, getState, { apiV3, payex }) => {
    const state = getState();
    const isFetchingDocuments = isFetching(state, 'documents');
    const service = shouldUsePayexService(state) ? payex : apiV3;

    if (isFetchingDocuments) {
      return;
    }

    dispatch({ type: DELETE_FILE_REQUEST });

    try {
      await service.payments.deleteFile({ url });

      return dispatch({
        type: DELETE_FILE_SUCCESS,
        payload: { fileId },
      });
    } catch (error) {
      if (isHttpErrorWithStatus(error)) {
        const _error = error as HttpError;
        const { status = '' } = _error;

        _error.message = `${DELETE_FILE_FAILURE} ${_error.message}`;

        errorNotifier.notifyWithFingerprint(_error, [
          DELETE_FILE_FAILURE,
          `${status}`,
        ]);
      }
    }
  };

export const fileNumberExceeded = () => ({
  type: UPLOAD_DOCUMENT_MAX_FILES_FAILURE,
});
